import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { getOrganization, getOrganizations } from "@/api/endpoints";
import { Badge, Card, Input, PageTitle } from "@/components/ui/primitives";
import type { Plan, PlanTier } from "@/types";
import { DataState } from "@/components/data-state";
import { Pagination } from "@/components/pagination";
import { useDebouncedValue } from "@/hooks/use-debounced-value";

const PAGE_SIZE = 25;

export function OrganizationsPage() {
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const query = useDebouncedValue(search.trim(), 300);

  const list = useQuery({
    queryKey: ["system-organizations", query, page],
    queryFn: () => getOrganizations({ query, page, limit: PAGE_SIZE }),
  });

  const detail = useQuery({
    queryKey: ["system-organization", selected],
    queryFn: () => getOrganization(selected!),
    enabled: selected != null,
  });

  return (
    <div className="space-y-4">
      <PageTitle>Organizasyonlar</PageTitle>

      <Input
        value={search}
        placeholder="Ara"
        onChange={(event) => {
          setSearch(event.target.value);
          setPage(0);
        }}
        className="max-w-sm"
      />

      <DataState
        isLoading={list.isLoading}
        isError={list.isError}
        isEmpty={(list.data?.items.length ?? 0) === 0}
        onRetry={() => void list.refetch()}
      >
        <Card className="divide-y divide-border">
          {list.data?.items.map((org) => (
            <button
              key={org.id}
              type="button"
              onClick={() => setSelected(selected === org.id ? null : org.id)}
              className="flex w-full items-center gap-3 p-3 text-left hover:bg-muted"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate font-medium">{org.name}</p>
                <p className="text-xs text-muted-foreground">
                  {new Date(org.createdAt).toLocaleDateString()} · seq {org.syncSeq}
                </p>
              </div>
              <PlanBadge plan={org.plan} tier={org.planTier} />
              <Badge>{org.members} üye</Badge>
              <Badge tone="primary">{org.companies} şirket</Badge>
            </button>
          ))}
        </Card>
        <Pagination
          page={page}
          limit={PAGE_SIZE}
          total={list.data?.total ?? 0}
          onChange={setPage}
        />
      </DataState>

      {selected && detail.data && (
        <Card className="space-y-3 p-4">
          <p className="font-medium">{detail.data.organization.name}</p>
          <div className="flex flex-wrap gap-2">
            {Object.entries(detail.data.counts).map(([key, value]) => (
              <Badge key={key}>
                {key}: {value}
              </Badge>
            ))}
          </div>
          <div className="divide-y divide-border rounded-md border border-border">
            {detail.data.members.map((member) => (
              <div key={member.userId} className="flex items-center gap-3 p-2 text-sm">
                <span className="min-w-0 flex-1 truncate">
                  {member.email ?? member.userId}
                </span>
                <Badge tone={member.role === "admin" ? "primary" : "neutral"}>
                  {member.role}
                </Badge>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}

/**
 * The row's plan at a glance.
 *
 * `readonly` and `purged` are the two an operator needs to spot without
 * reading: one is a customer whose subscription lapsed and whose cloud copy is
 * on a countdown, the other is one whose copy has already gone.
 */
function PlanBadge({ plan, tier }: { plan: Plan; tier: PlanTier | null }) {
  const label = tier ? `${plan} · ${tier}` : plan;

  const tone =
    plan === "premium"
      ? "success"
      : plan === "grace"
        ? "warning"
        : plan === "readonly" || plan === "purged"
          ? "danger"
          : "neutral";

  return <Badge tone={tone}>{label}</Badge>;
}
